// src/com/dc_temp_break_his/DcTempBreakHis.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { comInfoListRepo, dcTempBreakHisListRepo, userInfoRepo } from '../../value/AppModel';
import { LIST_DC_TEMP_BREAK_HIS } from './DcTempBreakHisGql';
import { DcTempBreakHisDictionary } from './DcTempBreakHisDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import ValueMisc from '../../value/ValueMisc';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import VzmSelector from '../../common/VzmSelector';

const CompanyLabel = styled(Box)({
    display:'flex', alignItems:'center',
    border: AppStyle.BorderCCC, borderRadius:5, padding:10,
    minHeight:40, boxSizing:'border-box',
    fontWeight:'bold'
});

/**
 * (DC_TEMP_BREAK_HIS) 정보 View Controller
 * 차량의 온도 이상 이력
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-12-05 15:00:51 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function DcTempBreakHis({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const dcTempBreakHisListData = useReactiveVar(dcTempBreakHisListRepo);
    const [countOfRecordsShowing, setCountOfRecordsShowing] = useState(0);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [companyOptions, setCompanyOptions] = useState([{comId:AppWord.ALL, comName:AppWord.AllKorean}]);
    const [searchText, setSearchText] = useState('');
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getDcTempBreakHisList, responseList] = useLazyQuery(LIST_DC_TEMP_BREAK_HIS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: DcTempBreakHisDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });

    const getDcTempBreakHisListNow = (date) => {
        const param = {variables:{date:date || ValueMisc.getYMD()}};
        getDcTempBreakHisList(param);
    };

    useEffect(() => {
        const today = dcTempBreakHisListData.date || ValueMisc.getYMD();
        let diff = new Date().getTime()/1000 - dcTempBreakHisListData.when;
        if(diff > 300 || today !== dcTempBreakHisListData.date) {
            if(today !== dcTempBreakHisListData.date) {
                dcTempBreakHisListRepo({...dcTempBreakHisListData, date:today});
            }
            getDcTempBreakHisListNow(today);
        }

        if(sessionInfo.isHomeplusUser()) {
            diff = new Date().getTime()/1000 - comInfoListData.when;
            if(diff > 300) {
                getComInfoList();
            }
        }
    }, []);

    // 운송사목록에 {comId:AppWord.ALL, comName:AppWord.AllKorean} 추가. 홈플러스 사용자일 경우에만 적용됨.(위 useEffect 참조)
    useEffect(()=>{
        if(comInfoListData.records.length > 0) {
            const options
            = comInfoListData.records
            .filter((com) => com.useYn === 'Y' && com.dcCd === sessionInfo.user.dscIdAs)
            .sort((a,b) => a.comName.localeCompare(b.comName))
            .map((com) => {
                return {comId:com.comId, comName:com.comName};
            });
            options.unshift({comId:AppWord.ALL, comName:AppWord.AllKorean});
            setCompanyOptions(options);
        }
    }, [comInfoListData]);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.dcTempBreakHisList) {
            dcTempBreakHisListRepo({
                ...dcTempBreakHisListData,
                when: new Date().getTime()/1000,
                records:data.dcTempBreakHisList.map((rec)=>{
                    rec.uniqueKey = `${rec.dcCd}_${rec.storeId}_${rec.comId}_${rec.vehPlates}_${rec.gpsDatetime}`;
                    return rec;
                })
            });
            lastReloadRef.current = new Date().getTime()/1000;
        }
    };

    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    const onChangeDate = (date)=>{
        // setDatePicked(date);
        const ymd = ValueMisc.getYMD(date.toDate());
        dcTempBreakHisListRepo({...dcTempBreakHisListData, date:ymd});
        getDcTempBreakHisListNow(ymd);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getDcTempBreakHisListNow(dcTempBreakHisListData.date);
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onCompanySelected = (id, value) => {
        const newData = {...dcTempBreakHisListData};
        newData.filter.comId = value ===AppWord.ALL ? null : value;
        dcTempBreakHisListRepo(newData); 
    };

    return(
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <ContentConainer>
                {viewServiceTags}
                <ContentHeader>
                    <ControlBox>
                        <DatePicker label={'날짜'}
                            sx={{width:150, marginRight:2}}
                            slotProps={{
                                openPickerIcon:{fontSize:AppWord.SMALL},
                                textField:{size:AppWord.SMALL}
                            }}
                            format='YYYY-MM-DD'
                            value={dcTempBreakHisListData.date ? dayjs(dcTempBreakHisListData.date) : ''}
                            onChange={onChangeDate}
                            disabled={responseList.loading}
                        />
                        {
                            sessionInfo.isTransCompanyUser()
                            ?
                            <CompanyLabel>
                                {sessionInfo.user.dscName}
                            </CompanyLabel>
                            :
                            <VzmSelector
                                dict={{
                                    id: 'comId',
                                    label: '운송사',
                                    labelKey: 'comName'
                                }}
                                value={dcTempBreakHisListData.filter.comId || AppWord.ALL}
                                onChange={onCompanySelected}
                                list={companyOptions}
                                style={{minWidth:140, marginRight: 10}}
                                disabled={responseComList.loading}
                            />
                        }
                        <SearchBox
                            value={searchText}
                            onChangeText={onChangeSearchText}
                            width={200}
                        />
                        {
                            dcTempBreakHisListData.records.length > 0 &&
                            <span
                                style={{color:'#f00', fontSize:'0.8rem', marginLeft:10}}
                            >
                                ※ 배송 중 기준온도를 벗어난 차량입니다. 하역 즉시 냉장고 이동 및 품질검사 요청바랍니다!
                            </span>
                        }
                    </ControlBox>
                    <ToolBox>
                        <CountLabel count={countOfRecordsShowing} />
                        {
                            responseList.loading || responseComList.loading ?
                            <CircularProgress size={20} />
                            :
                            <IconButton
                                color={AppStyle.mui.inherit}
                                onClick={onClickReload}
                                disabled={!dcTempBreakHisListData.date}
                            >
                                <CachedIcon />
                            </IconButton>
                        }
                    </ToolBox>
                </ContentHeader>
                <FlexyTable
                    name={DcTempBreakHisDictionary.tableProps.tableId}
                    tableProps={DcTempBreakHisDictionary.tableProps}
                    records={
                        dcTempBreakHisListData.records
                        .filter((rec) => {
                            if(!dcTempBreakHisListData.filter.comId) return true;
                            return rec.comId === dcTempBreakHisListData.filter.comId;
                        })
                    }
                    search={searchText}
                    tools={[]}
                    onChangeCountToDisplay={setCountOfRecordsShowing}
                />
            </ContentConainer>
        </LocalizationProvider>
    );
}
